// apps/web/src/admin/apiAdmin.ts
import type {
  AdminUsersListResponse, AdminAdjustRequest, AdminDisableRequest,
  AdminLiveOpsResponse, AdminRevenueResponse,
} from '@aslgo/shared';

function token(): string {
  const url = import.meta.env.VITE_SUPABASE_URL ?? '';
  const ref = url.includes('://') ? new URL(url).hostname.split('.')[0] : '';
  const raw = ref ? localStorage.getItem(`sb-${ref}-auth-token`) : null;
  if (!raw) throw new Error('not signed in');
  try { return JSON.parse(raw).access_token as string; } catch { throw new Error('bad session'); }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  // SPA is served at the host root; nginx routes /api/* to the backend.
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token()}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { msg = (await res.json()).error ?? msg; } catch { /* keep statusText */ }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export const apiAdmin = {
  listUsers(q?: string, role?: 'learner'|'guide', since?: string): Promise<AdminUsersListResponse> {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (role) params.set('role', role);
    if (since) params.set('since', since);
    const qs = params.toString();
    return call(`/api/admin/users${qs ? '?' + qs : ''}`);
  },
  userDetail(id: string): Promise<any> {
    return call(`/api/admin/users/${id}`);
  },
  promote(id: string)        { return call(`/api/admin/users/${id}/promote`, { method: 'POST' }); },
  revoke(id: string)         { return call(`/api/admin/users/${id}/revoke`,  { method: 'POST' }); },
  enable(id: string)         { return call(`/api/admin/users/${id}/enable`,  { method: 'POST' }); },
  disable(id: string, body: AdminDisableRequest) {
    return call(`/api/admin/users/${id}/disable`, { method: 'POST', body: JSON.stringify(body) });
  },
  adjust(id: string, body: AdminAdjustRequest) {
    return call(`/api/admin/users/${id}/adjust`,  { method: 'POST', body: JSON.stringify(body) });
  },
  reverse(id: string, body: { eventId: number; reason: string }) {
    return call(`/api/admin/users/${id}/reverse`, { method: 'POST', body: JSON.stringify(body) });
  },
  reverseSession(id: string, body: { sessionId: number; reason: string }) {
    return call(`/api/admin/users/${id}/reverse-session`, { method: 'POST', body: JSON.stringify(body) });
  },
  purge(id: string, body: { reason?: string } = {}): Promise<{ ok: true }> {
    return call(`/api/admin/users/${id}/purge`, { method: 'POST', body: JSON.stringify(body) });
  },
  liveOps(): Promise<AdminLiveOpsResponse> { return call('/api/admin/live-ops'); },
  endCall(room: string): Promise<unknown>  { return call(`/api/admin/calls/${encodeURIComponent(room)}/end`, { method: 'POST' }); },
  revenue(): Promise<AdminRevenueResponse> { return call('/api/admin/revenue'); },
  callActivity(): Promise<any> { return call('/api/admin/call-activity'); },
  guides(opts: { since?: string } = {}): Promise<any> {
    const qs = opts.since ? `?since=${encodeURIComponent(opts.since)}` : '';
    return call(`/api/admin/guides${qs}`);
  },
  logins(params: { email?: string; ip?: string; since?: string } = {}): Promise<any> {
    const p = new URLSearchParams();
    if (params.email) p.set('email', params.email);
    if (params.ip)    p.set('ip', params.ip);
    if (params.since) p.set('since', params.since);
    const qs = p.toString();
    return call(`/api/admin/logins${qs ? '?' + qs : ''}`);
  },
  systemHealth(): Promise<any> { return call('/api/admin/system-health'); },
  audit(params: { action?: string; actor?: string; target?: string; cursor?: string } = {}): Promise<any> {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) p.set(k, v);
    const qs = p.toString();
    return call(`/api/admin/audit${qs ? '?' + qs : ''}`);
  },
  applications(): Promise<{ applications: any[] }> { return call('/api/admin/applications'); },
  approveApplication(id: string) { return call(`/api/admin/applications/${id}/approve`, { method: 'POST' }); },
  rejectApplication(id: string, body: { reason: string }) {
    return call(`/api/admin/applications/${id}/reject`, { method: 'POST', body: JSON.stringify(body) });
  },
  approveVideo(id: string) { return call(`/api/admin/applications/${id}/approve-video`, { method: 'POST' }); },
  requestVideoChanges(id: string, body: { reason: string }) { return call(`/api/admin/applications/${id}/request-video-changes`, { method: 'POST', body: JSON.stringify(body) }); },
  ripplingFormsComplete(id: string) { return call(`/api/admin/applications/${id}/rippling-forms-complete`, { method: 'POST' }); },
  approveFinancial(id: string) { return call(`/api/admin/applications/${id}/approve-financial`, { method: 'POST' }); },
  requestFinancialChanges(id: string, body: { reason: string }) { return call(`/api/admin/applications/${id}/request-financial-changes`, { method: 'POST', body: JSON.stringify(body) }); },
  autoApprove(id: string, body: { reason: string }) {
    return call(`/api/admin/applications/${id}/auto-approve`, { method: 'POST', body: JSON.stringify(body) });
  },
  guideDocumentUrl(id: string, kind: 'video' | 'resume'): Promise<{ url: string }> { return call(`/api/admin/applications/${id}/document?kind=${kind}`); },
  integrity(): Promise<{ checks: { key: string; severity: string; violations: number; detail: any[] }[] }> {
    return call('/api/admin/integrity');
  },
};
